/**
 * PDP buy-kind bus (Feature 0) + fancy choice selector (Feature 4) +
 * ATC / qty / fulfillment property / trust chip (Feature 5) +
 * shipping accordion (Feature 6) + returns accordion (Feature 7) +
 * files included tree (Feature 8).
 * Reads [data-og-variant-buy-map] and keeps [data-og-product-buy] in sync with the
 * selected Empire variant so later features (accordions) share one source.
 */
(function () {
  function parseMap(root) {
    const el = root.querySelector('[data-og-variant-buy-map]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function selectedVariantId(root) {
    const selection = root.querySelector('[data-variant-selection]');
    if (selection) {
      const attr = selection.getAttribute('variant');
      if (attr && attr !== 'not-selected') return String(attr);
    }

    const select = root.querySelector('[data-variants]');
    if (select && select.value && select.value !== 'not-selected') {
      return String(select.value);
    }

    return root.getAttribute('data-og-variant-id') || null;
  }

  function syncChoiceButtons(root, variantId) {
    const options = root.querySelectorAll('[data-og-choice-option]');
    if (!options.length) return;

    options.forEach(function (btn) {
      const selected = String(btn.getAttribute('data-variant-id')) === String(variantId);
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
  }

  function syncFulfillmentProperty(root, state) {
    const input = root.querySelector('[data-og-fulfillment-property]');
    if (!input) return;

    const fulfillment = state.cart_fulfillment_type;
    if (fulfillment === 'physical' || fulfillment === 'digital') {
      input.value = fulfillment;
      input.disabled = false;
    } else {
      input.value = '';
      input.disabled = true;
    }
  }

  function syncQuantityLock(root, state) {
    const locked = Boolean(state.lock_quantity);
    const input = root.querySelector('.quantity-selector__input');
    if (!input) return;

    if (locked) {
      input.value = '1';
      input.setAttribute('readonly', 'readonly');
    } else {
      input.removeAttribute('readonly');
    }
  }

  function parseVariantPrices(root) {
    const el = root.querySelector('[data-og-variant-prices]');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      return {};
    }
  }

  function formatProductMoney(cents, root) {
    const amount = Number(cents);
    if (Number.isNaN(amount)) return '';
    const format =
      (root && root.getAttribute('data-og-money-format')) ||
      (window.theme && theme.moneyFormat) ||
      (window.Shopify && Shopify.money_format) ||
      null;
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && format) {
      return Shopify.formatMoney(amount, format).replace(/<\/?[^>]+>/g, '');
    }
    return (amount / 100).toFixed(2);
  }

  function resolveDisplayAmounts(price, compareAt, discountPct) {
    let compare = Number(compareAt) || 0;
    let current = Number(price) || 0;
    if (!compare) compare = current;
    const pct = Math.min(100, Math.max(0, Number(discountPct) || 0));
    if (pct > 0) {
      current = Math.ceil(current * (1 - pct / 100));
    }
    return {
      price: current,
      compare: compare,
      onSale: current < compare,
    };
  }

  function getStrikethroughPct(root) {
    let pct = Number(root.getAttribute('data-og-strikethrough-discount') || 0);
    if (pct <= 0) {
      const el = root.querySelector('[data-og-strikethrough-discount]');
      if (el) {
        pct = Number(el.getAttribute('data-og-strikethrough-discount') || 0);
      }
    }
    return pct;
  }

  function normalizeMoneyText(value) {
    return String(value || '').replace(/\s/g, '');
  }

  function applyStrikethroughPricing(root, variantId) {
    const pct = getStrikethroughPct(root);
    if (pct <= 0 || variantId == null) return false;

    const prices = parseVariantPrices(root);
    const entry = prices[String(variantId)];
    if (!entry) return false;

    const resolved = resolveDisplayAmounts(entry.price, entry.compare_at, pct);
    if (!resolved.onSale) return true;

    const pricing = root.querySelector('[data-product-pricing]');
    if (!pricing) return false;

    const formattedPrice = formatProductMoney(resolved.price, root);
    const formattedCompare = formatProductMoney(resolved.compare, root);
    const compareContainer = pricing.querySelector('[data-price-compare-container]');
    const compareEl = pricing.querySelector('[data-price-compare]');
    const priceEl = pricing.querySelector('[data-price]');
    const current = pricing.querySelector('[data-price-container]');

    const needsFix =
      !compareContainer ||
      !compareContainer.classList.contains('visible') ||
      (compareEl &&
        normalizeMoneyText(compareEl.textContent) !== normalizeMoneyText(formattedCompare)) ||
      (priceEl && normalizeMoneyText(priceEl.textContent) !== normalizeMoneyText(formattedPrice));

    if (!needsFix) return true;

    root.__ogStrikeApplying = true;
    try {
      if (priceEl) {
        priceEl.textContent = formattedPrice;
      }
      if (compareEl) {
        compareEl.textContent = formattedCompare;
      }
      if (compareContainer) {
        compareContainer.classList.add('visible');
      }
      if (current) {
        current.classList.add('price__current--on-sale');
      }

      const hiddenCompare = pricing.querySelector('[data-compare-price-hidden] [data-price-compare]');
      const hiddenPrice = pricing.querySelector('[data-current-price-hidden] [data-price]');
      if (hiddenCompare) {
        hiddenCompare.textContent = formattedCompare;
      }
      if (hiddenPrice) {
        hiddenPrice.textContent = formattedPrice;
      }
    } finally {
      root.__ogStrikeApplying = false;
    }

    return true;
  }

  function syncStrikethroughPricing(root, state) {
    const variantId =
      (state && state.variant_id != null ? String(state.variant_id) : null) ||
      selectedVariantId(root);
    applyStrikethroughPricing(root, variantId);
  }

  function scheduleStrikethroughRetries(root, variantId) {
    if (getStrikethroughPct(root) <= 0) return;

    const delays = [0, 50, 150, 400];
    delays.forEach(function (delay) {
      window.setTimeout(function () {
        const id = variantId != null ? String(variantId) : selectedVariantId(root);
        applyStrikethroughPricing(root, id);
      }, delay);
    });
  }

  function watchStrikethroughPricing(root) {
    if (root.getAttribute('data-og-strike-watch')) return;
    if (getStrikethroughPct(root) <= 0) return;

    const pricing = root.querySelector('[data-product-pricing]');
    if (!pricing || typeof MutationObserver !== 'function') return;

    root.setAttribute('data-og-strike-watch', 'true');

    let rafId = null;
    const schedule = function () {
      if (root.__ogStrikeApplying) return;
      if (rafId) return;
      rafId = window.requestAnimationFrame(function () {
        rafId = null;
        applyStrikethroughPricing(root, selectedVariantId(root));
      });
    };

    const observer = new MutationObserver(schedule);
    observer.observe(pricing, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    const paymentTarget = root.querySelector('[data-payment-terms-target]');
    if (paymentTarget) {
      observer.observe(paymentTarget, { childList: true, subtree: true });
    }
  }

  function parseCartVariantIds(root) {
    const el = root.querySelector('[data-og-cart-variant-ids]');
    if (!el) return [];
    try {
      const parsed = JSON.parse(el.textContent);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(String);
    } catch (e) {
      return [];
    }
  }

  function getCartVariantIds(root) {
    if (root.__ogCartVariantIds) return root.__ogCartVariantIds;
    root.__ogCartVariantIds = parseCartVariantIds(root);
    return root.__ogCartVariantIds;
  }

  function setCartVariantIds(root, ids) {
    root.__ogCartVariantIds = (ids || []).map(String);
    const el = root.querySelector('[data-og-cart-variant-ids]');
    if (el) {
      el.textContent = JSON.stringify(root.__ogCartVariantIds.map(Number));
    }
  }

  function isVariantInCart(root, variantId) {
    if (variantId == null) return false;
    return getCartVariantIds(root).indexOf(String(variantId)) !== -1;
  }

  function isSelectedVariantAvailable(root, variantId) {
    const select = root.querySelector('[data-variants]');
    if (!select || variantId == null) return true;
    const option = select.querySelector('option[value="' + String(variantId) + '"]');
    if (!option) return true;
    return !option.disabled;
  }

  function syncPaymentButton(root, hide) {
    const wrap = root.querySelector('[data-og-payment-button]');
    if (!wrap) return;
    wrap.classList.toggle('is-hidden', Boolean(hide));
    if (hide) {
      wrap.setAttribute('hidden', '');
    } else {
      wrap.removeAttribute('hidden');
    }
  }

  function syncAtcButton(root, state) {
    const button = root.querySelector('[data-og-atc-button]');
    if (!button) return;

    const fulfillment = state.cart_fulfillment_type;
    button.classList.toggle('product-form--atc-button-digital', fulfillment === 'digital');
    button.classList.toggle('product-form--atc-button-physical', fulfillment === 'physical');

    const textEl = button.querySelector('.atc-button--text');
    const defaultLabel = button.getAttribute('data-og-atc-label-default') || 'Add to cart';
    const soldOutLabel = button.getAttribute('data-og-atc-label-sold-out') || 'Sold out';
    const inCartLabel = button.getAttribute('data-og-atc-label-in-cart') || 'Added to cart';
    const preorderLabel = button.getAttribute('data-og-atc-label-preorder') || defaultLabel;
    const isPreorder = button.hasAttribute('data-product-atc-preorder');

    const available = isSelectedVariantAvailable(root, state.variant_id);
    const inCart = fulfillment === 'digital' && isVariantInCart(root, state.variant_id);

    button.classList.toggle('product-form--atc-button-in-cart', inCart && available);

    if (!available) {
      button.disabled = true;
      button.classList.add('disabled');
      button.setAttribute('data-og-atc-locked', 'sold-out');
      if (textEl) textEl.textContent = soldOutLabel;
      syncPaymentButton(root, true);
      return;
    }

    if (inCart) {
      button.disabled = true;
      button.classList.add('disabled');
      button.setAttribute('data-og-atc-locked', 'in-cart');
      if (textEl) textEl.textContent = inCartLabel;
      syncPaymentButton(root, true);
      return;
    }

    button.disabled = false;
    button.classList.remove('disabled');
    button.removeAttribute('data-og-atc-locked');
    if (textEl) textEl.textContent = isPreorder ? preorderLabel : defaultLabel;
    syncPaymentButton(root, false);
  }

  function refreshCartVariantIds(roots) {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('cart');
        return response.json();
      })
      .then(function (cart) {
        const ids = (cart.items || []).map(function (item) {
          return item.variant_id;
        });
        (roots || document.querySelectorAll('[data-og-product-buy]')).forEach(function (root) {
          setCartVariantIds(root, ids);
          const map = parseMap(root) || {};
          sync(root, map);
        });
      })
      .catch(function () {
        /* ignore */
      });
  }

  function syncTrustChip(root, state) {
    const chip = root.querySelector('[data-og-trust-chip]');
    if (!chip) return;

    // Only Games production (3d-parts): same gate as show_returns / F7 returns accordion
    const show = Boolean(state.show_returns);
    chip.classList.toggle('is-hidden', !show);
    if (show) {
      chip.removeAttribute('hidden');
    } else {
      chip.setAttribute('hidden', '');
    }
  }

  function formatMoney(cents, format) {
    const amount = (Number(cents) / 100).toFixed(2);
    if (!format) return amount;

    const value = format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(Number(cents) / 100)))
      .replace(/\{\{\s*amount\s*\}\}/g, amount);

    return value.replace(/<\/?[^>]+>/g, '');
  }

  function convertGbpCosts(scope) {
    const root = scope || document;
    const shipping = root.matches && root.matches('[data-og-shipping]')
      ? root
      : root.querySelector('[data-og-shipping]');
    if (!shipping) return;

    const format =
      shipping.getAttribute('data-og-money-format') ||
      (window.theme && theme.moneyFormat) ||
      null;
    const rate =
      window.Shopify && Shopify.currency && Shopify.currency.rate != null
        ? Number(Shopify.currency.rate)
        : 1;

    shipping.querySelectorAll('[data-og-gbp-cost]').forEach(function (el) {
      const gbp = Number(el.getAttribute('data-og-gbp-cost'));
      if (Number.isNaN(gbp)) return;
      const cents = Math.round(gbp * 100 * rate);
      if (window.Shopify && typeof Shopify.formatMoney === 'function' && format) {
        el.textContent = Shopify.formatMoney(cents, format).replace(/<\/?[^>]+>/g, '');
      } else {
        el.textContent = formatMoney(cents, format);
      }
    });
  }

  function syncShipping(root, state) {
    const shipping = root.querySelector('[data-og-shipping]');
    if (!shipping) return;

    const vendorSlug = state.vendor_slug || '';
    const panels = shipping.querySelectorAll('[data-og-shipping-panel]');
    let matched = false;

    panels.forEach(function (panel) {
      const active = panel.getAttribute('data-og-shipping-vendor') === vendorSlug;
      panel.classList.toggle('is-active', active);
      if (active) {
        panel.removeAttribute('hidden');
        matched = true;
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    const show = Boolean(state.show_shipping) && matched;
    shipping.classList.toggle('is-hidden', !show);
    if (show) {
      shipping.removeAttribute('hidden');
      convertGbpCosts(shipping);
    } else {
      shipping.setAttribute('hidden', '');
      const details = shipping.querySelector('details');
      if (details) details.open = false;
    }

    const empty = shipping.querySelector('[data-og-shipping-empty]');
    if (empty) empty.setAttribute('hidden', '');
  }

  function syncReturns(root, state) {
    const show = Boolean(state.show_returns);
    root.querySelectorAll('[data-og-returns]').forEach(function (el) {
      el.classList.toggle('is-hidden', !show);
      if (show) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
        const details = el.querySelector('details');
        if (details) details.open = false;
      }
    });
  }

  function formatFileSize(bytes) {
    const size = Number(bytes);
    if (!size || size < 0 || Number.isNaN(size)) return '';
    if (size < 1024) return size + ' B';
    if (size < 1024 * 1024) return (size / 1024).toFixed(size < 10 * 1024 ? 1 : 0) + ' KB';
    return (size / (1024 * 1024)).toFixed(size < 10 * 1024 * 1024 ? 1 : 0) + ' MB';
  }

  function normalizeFileLabel(value) {
    let label = String(value == null ? '' : value);

    try {
      if (/%[0-9A-Fa-f]{2}/.test(label)) {
        label = decodeURIComponent(label.replace(/\+/g, '%20'));
      }
    } catch (e) {
      /* keep original */
    }

    // Repair common UTF-8-as-Latin-1 mojibake (e.g. Ã‘ → Ñ, Ã± → ñ)
    try {
      if (/Ã[\x80-\xBF]/.test(label)) {
        const bytes = Uint8Array.from(label, function (ch) {
          return ch.charCodeAt(0) & 0xff;
        });
        const repaired = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
        if (repaired && repaired.indexOf('\uFFFD') === -1) {
          label = repaired;
        }
      }
    } catch (e) {
      /* keep original */
    }

    if (typeof label.normalize === 'function') {
      label = label.normalize('NFC');
    }

    return label;
  }

  function truncateFileName(name, maxLen) {
    const max = maxLen || 64;
    if (name.length <= max) return name;

    const extMatch = name.match(/(\.[A-Za-z0-9]{1,8})$/);
    const ext = extMatch ? extMatch[1] : '';
    const base = ext ? name.slice(0, -ext.length) : name;
    const budget = Math.max(12, max - ext.length - 1);
    const head = Math.ceil(budget * 0.58);
    const tail = Math.max(4, budget - head);

    return base.slice(0, head) + '…' + base.slice(-tail) + ext;
  }

  function buildFilesTree(files) {
    const root = { folders: {}, files: [] };
    (files || []).forEach(function (file) {
      if (!file) return;
      const fileName = normalizeFileLabel(file.fileName);
      if (!fileName) return;

      const key = normalizeFileLabel(file.fileKey || '')
        .replace(/^\/+|\/+$/g, '')
        .trim();
      const parts = key ? key.split('/').filter(Boolean) : [];
      let node = root;
      parts.forEach(function (part) {
        const folderName = normalizeFileLabel(part);
        if (!node.folders[folderName]) {
          node.folders[folderName] = { folders: {}, files: [] };
        }
        node = node.folders[folderName];
      });
      node.files.push({
        fileName: fileName,
        fileSizeBytes: file.fileSizeBytes,
      });
    });
    return root;
  }

  function appendFilesNode(parent, node) {
    const folderNames = Object.keys(node.folders).sort(function (a, b) {
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    });
    const files = (node.files || []).slice().sort(function (a, b) {
      return String(a.fileName || '').localeCompare(String(b.fileName || ''), undefined, {
        sensitivity: 'base',
      });
    });

    if (!folderNames.length && !files.length) return;

    const list = document.createElement('ul');
    list.className = 'product-og-files__list';

    folderNames.forEach(function (name) {
      const li = document.createElement('li');
      li.className = 'product-og-files__folder';

      const details = document.createElement('details');
      details.className = 'product-og-files__folder-details';

      const summary = document.createElement('summary');
      summary.className = 'product-og-files__folder-summary';
      summary.title = name;

      const nameEl = document.createElement('span');
      nameEl.className = 'product-og-files__folder-name';
      nameEl.textContent = truncateFileName(name, 48);
      summary.appendChild(nameEl);

      details.appendChild(summary);
      appendFilesNode(details, node.folders[name]);
      li.appendChild(details);
      list.appendChild(li);
    });

    files.forEach(function (file) {
      const li = document.createElement('li');
      li.className = 'product-og-files__file';

      const nameEl = document.createElement('span');
      nameEl.className = 'product-og-files__file-name';
      nameEl.textContent = truncateFileName(file.fileName, 72);
      nameEl.title = file.fileName;
      li.appendChild(nameEl);

      const sizeLabel = formatFileSize(file.fileSizeBytes);
      if (sizeLabel) {
        const sizeEl = document.createElement('span');
        sizeEl.className = 'product-og-files__file-size';
        sizeEl.textContent = sizeLabel;
        li.appendChild(sizeEl);
      }

      list.appendChild(li);
    });

    parent.appendChild(list);
  }

  function decodeBase64Utf8(value) {
    if (!value) return '';
    try {
      const binary = atob(value);
      if (typeof TextDecoder !== 'undefined') {
        const bytes = Uint8Array.from(binary, function (ch) {
          return ch.charCodeAt(0);
        });
        return new TextDecoder('utf-8').decode(bytes);
      }
      return decodeURIComponent(escape(binary));
    } catch (e) {
      return '';
    }
  }

  function parseFilesMap(filesRoot) {
    const el = filesRoot.querySelector('[data-og-files-map]');
    if (!el) return {};

    const b64 = el.getAttribute('data-og-files-b64');
    if (b64) {
      try {
        const decoded = decodeBase64Utf8(b64);
        const parsed = JSON.parse(decoded);
        if (typeof parsed === 'string') return JSON.parse(parsed);
        return parsed || {};
      } catch (e) {
        /* fall through to textContent */
      }
    }

    try {
      const raw = (el.textContent || '').replace(/^\uFEFF/, '').trim();
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'string') return JSON.parse(parsed);
      return parsed || {};
    } catch (e) {
      return {};
    }
  }

  function syncFiles(root, state) {
    const filesRoot = root.querySelector('[data-og-files]');
    if (!filesRoot) return;

    const map = parseFilesMap(filesRoot);
    const variantId = state.variant_id != null ? String(state.variant_id) : '';
    let files = (variantId && map[variantId]) || [];
    if (typeof files === 'string') {
      try {
        files = JSON.parse(files);
      } catch (e) {
        files = [];
      }
    }
    const hasFiles = Array.isArray(files) && files.length > 0;
    const show = Boolean(state.show_files) && hasFiles;

    filesRoot.classList.toggle('is-hidden', !show);
    if (show) {
      filesRoot.removeAttribute('hidden');
    } else {
      filesRoot.setAttribute('hidden', '');
      const details = filesRoot.querySelector('details.product-og-files');
      if (details) details.open = false;
    }

    const tree = filesRoot.querySelector('[data-og-files-tree]');
    const empty = filesRoot.querySelector('[data-og-files-empty]');
    if (tree) {
      tree.setAttribute('data-og-active-variant', variantId);
      while (tree.firstChild) tree.removeChild(tree.firstChild);
      if (hasFiles) {
        appendFilesNode(tree, buildFilesTree(files));
      }
    }
    if (empty) {
      if (state.show_files && !hasFiles) {
        empty.removeAttribute('hidden');
      } else {
        empty.setAttribute('hidden', '');
      }
    }
  }

  function openReturnsAccordion(root) {
    const dedicated = root.querySelector('[data-og-returns-accordion]');
    if (dedicated) {
      if (dedicated.tagName === 'DETAILS') {
        dedicated.open = true;
      }
      const wrap = dedicated.closest('[data-og-returns]');
      if (wrap && wrap.hasAttribute('hidden')) return;
      dedicated.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    const tabs = root.querySelectorAll('details.collapsible-tab');
    for (let i = 0; i < tabs.length; i += 1) {
      const summary = tabs[i].querySelector('.collapsible-tab__heading');
      const label = summary ? summary.textContent || '' : '';
      if (/return/i.test(label)) {
        tabs[i].open = true;
        tabs[i].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return;
      }
    }
  }

  function applyState(root, state) {
    if (!state) return;

    root.setAttribute('data-og-buy-kind', state.buy_kind || 'default');
    root.setAttribute('data-og-cart-fulfillment', state.cart_fulfillment_type || 'default');
    root.setAttribute('data-og-variant-id', state.variant_id != null ? String(state.variant_id) : '');
    root.setAttribute('data-og-show-shipping', state.show_shipping ? 'true' : 'false');
    root.setAttribute('data-og-show-returns', state.show_returns ? 'true' : 'false');
    root.setAttribute('data-og-show-files', state.show_files ? 'true' : 'false');
    root.setAttribute('data-og-lock-quantity', state.lock_quantity ? 'true' : 'false');
    root.setAttribute('data-og-hide-standalone-price', state.hide_standalone_price ? 'true' : 'false');
    if (state.vendor_slug) {
      root.setAttribute('data-og-vendor-slug', state.vendor_slug);
    } else {
      root.removeAttribute('data-og-vendor-slug');
    }

    root.classList.toggle('is-og-physical', state.cart_fulfillment_type === 'physical');
    root.classList.toggle('is-og-digital', state.cart_fulfillment_type === 'digital');
    root.classList.toggle('is-og-choice', Boolean(state.is_choice));
    root.classList.toggle('is-og-hide-price', Boolean(state.hide_standalone_price));
    root.classList.toggle('is-og-lock-quantity', Boolean(state.lock_quantity));
    root.classList.toggle('is-og-show-files', Boolean(state.show_files));
    root.classList.toggle('is-og-show-shipping', Boolean(state.show_shipping));
    root.classList.toggle('is-og-show-returns', Boolean(state.show_returns));

    if (state.variant_id != null) {
      syncChoiceButtons(root, state.variant_id);
    }

    syncFulfillmentProperty(root, state);
    syncQuantityLock(root, state);
    syncAtcButton(root, state);
    syncTrustChip(root, state);
    syncShipping(root, state);
    syncReturns(root, state);
    syncFiles(root, state);
    syncStrikethroughPricing(root, state);

    root.dispatchEvent(
      new CustomEvent('og:buy-kind-change', {
        bubbles: true,
        detail: state,
      })
    );
  }

  function parseSelectedState(root) {
    const el = root.querySelector('[data-og-selected-buy-state]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function sync(root, map) {
    const id = selectedVariantId(root);
    let state = (id && map[id]) || null;
    if (!state) {
      state = parseSelectedState(root);
    }
    if (state) applyState(root, state);
  }

  function setVariant(root, variantId) {
    if (!variantId) return;

    const select = root.querySelector('[data-variants]');
    if (select) {
      select.value = String(variantId);
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const selection = root.querySelector('[data-variant-selection]');
    if (selection) {
      selection.setAttribute('variant', String(variantId));
    }

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variantId));
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      /* ignore */
    }
  }

  function bindChoiceSelector(root, map) {
    const selector = root.querySelector('[data-og-choice-selector]');
    if (!selector || selector.getAttribute('data-og-choice-bound')) return;
    selector.setAttribute('data-og-choice-bound', 'true');

    selector.addEventListener('click', function (event) {
      const option = event.target.closest('[data-og-choice-option]');
      if (!option || !selector.contains(option)) return;
      event.preventDefault();
      const variantId = option.getAttribute('data-variant-id');
      if (!variantId) return;
      setVariant(root, variantId);
      sync(root, map);
    });
  }

  function bindTrustChip(root) {
    const chip = root.querySelector('[data-og-trust-chip]');
    if (!chip || chip.getAttribute('data-og-trust-bound')) return;
    chip.setAttribute('data-og-trust-bound', 'true');

    chip.addEventListener('click', function (event) {
      event.preventDefault();
      openReturnsAccordion(root);
    });
  }

  function bindAtcGuard(root) {
    if (root.getAttribute('data-og-atc-guard-bound')) return;
    root.setAttribute('data-og-atc-guard-bound', 'true');

    const form = root.querySelector('form[action*="/cart/add"]') || root.querySelector('form');
    if (!form) return;

    form.addEventListener(
      'submit',
      function (event) {
        const button = root.querySelector('[data-og-atc-button]');
        if (button && button.getAttribute('data-og-atc-locked') === 'in-cart') {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  }

  function bind(root) {
    if (root.getAttribute('data-og-buy-bound')) return;

    const map = parseMap(root) || {};
    root.setAttribute('data-og-buy-bound', 'true');
    getCartVariantIds(root);

    const onChange = function (event) {
      const variantId =
        (event &&
          event.detail &&
          event.detail.variant &&
          event.detail.variant.id != null &&
          event.detail.variant.id) ||
        selectedVariantId(root);

      sync(root, map);
      scheduleStrikethroughRetries(root, variantId);
    };

    const select = root.querySelector('[data-variants]');
    if (select) {
      select.addEventListener('change', onChange);
    }

    const selection = root.querySelector('[data-variant-selection]');
    if (selection) {
      selection.addEventListener('variant-change', onChange);
      if (typeof MutationObserver === 'function') {
        const observer = new MutationObserver(onChange);
        observer.observe(selection, { attributes: true, attributeFilter: ['variant'] });
      }
    }

    bindChoiceSelector(root, map);
    bindTrustChip(root);
    bindAtcGuard(root);
    watchStrikethroughPricing(root);
    sync(root, map);
    scheduleStrikethroughRetries(root, selectedVariantId(root));
  }

  function bindAll(scope) {
    const root = scope || document;
    root.querySelectorAll('[data-og-product-buy]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bindAll();
    });
  } else {
    bindAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    bindAll(event.target);
  });

  document.addEventListener('cart:updated', function () {
    refreshCartVariantIds();
  });
})();
